import React, { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, Plus, Trash2, RotateCcw, FolderPlus } from "lucide-react";
import { toast } from "sonner";

const GROUP_SHORT = { gst_monthly: "GST", gst_qrmp: "GST QRMP", tds: "TDS", it_nonaudit: "IT", it_audit: "IT audit", advance_tax: "Adv. tax", roc_company: "ROC Co.", roc_llp: "ROC LLP", pf_esi: "PF/ESI" };
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const quarterFrom = (m) => [0, 3, 6, 9].map((k) => ((m - 1 + k) % 12) + 1).sort((a, b) => a - b);

const MonthField = ({ item, onChange, testid }) => {
  if (item.frequency === "monthly") return <span className="text-xs text-slate-500">every month</span>;
  const value = String(item.months[0] || 1);
  return (
    <Select value={value} onValueChange={(v) => onChange(item.frequency === "quarterly" ? quarterFrom(Number(v)) : [Number(v)])}>
      <SelectTrigger data-testid={testid} className="h-8 text-xs"><SelectValue /></SelectTrigger>
      <SelectContent>
        {(item.frequency === "quarterly" ? [1, 2, 3] : MONTHS.map((_, i) => i + 1)).map((m) => (
          <SelectItem key={m} value={String(m)}>{item.frequency === "quarterly" ? quarterFrom(m).map((x) => MONTHS[x - 1]).join("/") : MONTHS[m - 1]}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const ItemRow = ({ item, idx, update, remove, readOnly }) => (
  <div className="grid grid-cols-12 gap-2 items-center py-1.5" data-testid={`cct-row-${idx}`}>
    <Input data-testid={`cct-name-${idx}`} disabled={readOnly} value={item.name} onChange={(e) => update({ name: e.target.value })} className="col-span-12 md:col-span-3 h-8 text-xs" />
    <Select disabled={readOnly} value={item.frequency} onValueChange={(v) => update({ frequency: v, months: v === "monthly" ? [] : v === "quarterly" ? quarterFrom(1) : [item.months[0] || 3] })}>
      <SelectTrigger data-testid={`cct-freq-${idx}`} className="col-span-4 md:col-span-2 h-8 text-xs"><SelectValue /></SelectTrigger>
      <SelectContent>{["monthly", "quarterly", "yearly"].map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}</SelectContent>
    </Select>
    <Input data-testid={`cct-day-${idx}`} disabled={readOnly} type="number" min={1} max={31} value={item.day} onChange={(e) => update({ day: Number(e.target.value) })} className="col-span-2 md:col-span-1 h-8 text-xs mono" title="Day of month" />
    <div className="col-span-6 md:col-span-2">{readOnly ? <span className="text-xs text-slate-500">{item.months.map((m) => MONTHS[m - 1]).join("/") || "every month"}</span> : <MonthField item={item} testid={`cct-month-${idx}`} onChange={(months) => update({ months })} />}</div>
    <Input data-testid={`cct-fee-${idx}`} disabled={readOnly} type="number" min={0} placeholder="Fee ₹" value={item.fee ?? ""}
      onChange={(e) => update({ fee: e.target.value === "" ? null : Number(e.target.value) })} className="col-span-4 md:col-span-1 h-8 text-xs mono" title="Standard fee (₹)" />
    <div className="col-span-6 md:col-span-2 flex items-center gap-2">
      <Switch data-testid={`cct-enabled-${idx}`} disabled={readOnly} checked={item.enabled} onCheckedChange={(v) => update({ enabled: v })} />
      <span className="text-[11px] text-slate-500">{item.enabled ? "In use" : "Off"}</span>
    </div>
    {!readOnly && <Button size="icon" variant="ghost" data-testid={`cct-delete-${idx}`} onClick={remove} className="col-span-2 md:col-span-1 h-8 w-8"><Trash2 className="w-4 h-4" /></Button>}
  </div>
);

const GroupHeader = ({ group, onChange, onAdd, onDelete, isAdmin }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid={`cct-group-header-${group.key}`}>
    {isAdmin ? (
      <Input data-testid={`cct-group-name-${group.key}`} value={group.label} maxLength={60} onChange={(e) => onChange({ label: e.target.value })}
        className="h-8 max-w-xs text-xs font-semibold uppercase tracking-wider text-slate-700" title="Rename this group" />
    ) : <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">{group.label}</div>}
    {!group.builtin && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">Custom</span>}
    {isAdmin && (
      <div className="ml-auto flex items-center gap-2">
        <Switch data-testid={`cct-group-enabled-${group.key}`} checked={group.enabled} onCheckedChange={(v) => onChange({ enabled: v })} />
        <span className="text-[11px] text-slate-500 w-20">{group.enabled ? "Group on" : "Group off"}</span>
        <Button size="sm" variant="ghost" data-testid={`cct-add-${group.key}`} onClick={onAdd} className="h-7 gap-1 text-xs"><Plus className="w-3 h-3" />Add item</Button>
        {!group.builtin && <Button size="icon" variant="ghost" data-testid={`cct-group-delete-${group.key}`} onClick={onDelete} className="h-7 w-7 text-red-600" title="Delete group"><Trash2 className="w-4 h-4" /></Button>}
      </div>
    )}
  </div>
);

const FeeOverrides = ({ items, isAdmin, labels }) => {
  const [rows, setRows] = useState(null);
  const [entities, setEntities] = useState([]);
  useEffect(() => {
    api.get("/compliance/fee-overrides").then((r) => setRows(r.data)).catch((e) => toast.error(formatErr(e)));
    if (isAdmin) api.get("/admin/portal/entities").then((r) => setEntities(r.data)).catch(() => {});
  }, [isAdmin]);
  const set = (i, patch) => setRows((l) => l.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const save = async () => {
    if (rows.some((r) => !r.entity || !r.key || r.fee === "" || r.fee == null)) return toast.error("Fill client, filing and fee on every row");
    try { await api.put("/compliance/fee-overrides", { items: rows.map((r) => ({ ...r, fee: Number(r.fee) })) }); toast.success("Client fee overrides saved"); } catch (e) { toast.error(formatErr(e)); }
  };
  if (!rows) return null;
  return (
    <div className="border-t border-slate-200 p-5" data-testid="fee-overrides">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Client fee overrides</div>
          <div className="text-[11px] text-slate-500">Used instead of the standard fee for that client. Fee used when marking “To invoice”: client override → standard fee → blank.</div>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" data-testid="fee-ov-add" onClick={() => setRows((l) => [...l, { entity: "", key: "", fee: "" }])} className="h-7 gap-1 text-xs"><Plus className="w-3 h-3" />Add override</Button>
            <Button size="sm" data-testid="fee-ov-save" onClick={save} className="bg-blue-700 hover:bg-blue-800 text-white h-7">Save overrides</Button>
          </div>
        )}
      </div>
      {rows.length === 0 && <div className="text-xs text-slate-400 mt-3" data-testid="fee-ov-empty">No overrides.</div>}
      <div className="mt-3 space-y-2">
        {rows.map((r, i) => (
          <div key={`ov-${i}`} className="grid grid-cols-12 gap-2 items-center" data-testid={`fee-ov-row-${i}`}>
            <Select disabled={!isAdmin} value={r.entity} onValueChange={(v) => set(i, { entity: v })}>
              <SelectTrigger data-testid={`fee-ov-entity-${i}`} className="col-span-12 md:col-span-4 h-8 text-xs"><SelectValue placeholder="Client" /></SelectTrigger>
              <SelectContent>{[...new Set([...entities, r.entity].filter(Boolean))].map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
            <Select disabled={!isAdmin} value={r.key} onValueChange={(v) => set(i, { key: v })}>
              <SelectTrigger data-testid={`fee-ov-key-${i}`} className="col-span-12 md:col-span-5 h-8 text-xs"><SelectValue placeholder="Filing" /></SelectTrigger>
              <SelectContent>{items.map((it) => <SelectItem key={it.key || it.name} value={it.key || `new-${it.name}`} disabled={!it.key}>{(labels[it.group] || GROUP_SHORT[it.group] || it.group)} · {it.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid={`fee-ov-fee-${i}`} disabled={!isAdmin} type="number" min={0} value={r.fee} onChange={(e) => set(i, { fee: e.target.value })} placeholder="Fee ₹" className="col-span-8 md:col-span-2 h-8 text-xs mono" />
            {isAdmin && <Button size="icon" variant="ghost" data-testid={`fee-ov-delete-${i}`} onClick={() => setRows((l) => l.filter((_, k) => k !== i))} className="col-span-4 md:col-span-1 h-8 w-8"><Trash2 className="w-4 h-4" /></Button>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function ComplianceTemplateEditor() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    try { const r = await api.get("/compliance/template"); setData(r.data); setItems(r.data.items); setGroups(r.data.group_list); } catch (e) { toast.error(formatErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const update = (i, patch) => setItems((l) => l.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const updateGroup = (key, patch) => setGroups((l) => l.map((g) => (g.key === key ? { ...g, ...patch } : g)));
  const addGroup = () => setGroups((l) => [...l, { key: `grp_${Date.now().toString(36)}`, label: `New group ${l.length + 1}`, enabled: true, builtin: false }]);
  const deleteGroup = (g) => {
    const n = items.filter((i) => i.group === g.key).length;
    if (!window.confirm(`Delete group “${g.label}”${n ? ` and its ${n} item(s)` : ""}? Existing reminders are not affected.`)) return;
    setGroups((l) => l.filter((x) => x.key !== g.key));
    setItems((l) => l.filter((i) => i.group !== g.key));
  };
  const addItem = (group) => setItems((l) => [...l, { key: null, group, name: "New item", frequency: "yearly", day: 15, months: [4], notes: "", enabled: true }]);
  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/compliance/template", { items, groups: groups.map(({ key, label, enabled }) => ({ key, label, enabled })) }); setItems(r.data.items); setGroups(r.data.group_list); toast.success("Compliance calendar saved"); } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };
  const reset = async () => {
    try { const r = await api.post("/compliance/template/reset"); setItems(r.data.items); setGroups(r.data.group_list); toast.success("Restored the standard list"); } catch (e) { toast.error(formatErr(e)); }
  };
  if (!data) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-md mt-6" data-testid="compliance-template-editor">
      <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center gap-2">
        <CalendarClock className="w-4 h-4" />
        <div className="text-sm font-semibold tracking-tight">Compliance calendar</div>
        <span className="text-xs text-slate-500 order-last w-full">Standard deadlines used by “Add compliance calendar” in the Compliance register. Rename groups and items, add your own groups, or switch groups off. Changes apply to future additions only.</span>
        {isAdmin && (
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" data-testid="cct-reset" onClick={reset} className="gap-1"><RotateCcw className="w-3 h-3" />Reset to standard</Button>
            <Button size="sm" data-testid="cct-save" disabled={saving} onClick={save} className="bg-blue-700 hover:bg-blue-800 text-white">{saving ? "Saving…" : "Save changes"}</Button>
          </div>
        )}
      </div>
      <div className="p-5 space-y-5">
        <div className="hidden md:grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-slate-400">
          <div className="col-span-3">Item</div><div className="col-span-2">Repeats</div><div className="col-span-1">Day</div><div className="col-span-2">Month(s)</div><div className="col-span-1">Std fee ₹</div><div className="col-span-2">Status</div>
        </div>
        {groups.map((grp) => (
          <div key={grp.key} data-testid={`cct-group-${grp.key}`} className={grp.enabled ? "" : "opacity-60"}>
            <GroupHeader group={grp} isAdmin={isAdmin} onChange={(p) => updateGroup(grp.key, p)} onAdd={() => addItem(grp.key)} onDelete={() => deleteGroup(grp)} />
            {!items.some((it) => it.group === grp.key) && <div className="text-xs text-slate-400 py-2" data-testid={`cct-group-empty-${grp.key}`}>No items yet. Use “Add item”.</div>}
            {items.map((it, i) => (it.group === grp.key ? (
              <ItemRow key={it.key || `new-${i}`} item={it} idx={i} readOnly={!isAdmin} update={(p) => update(i, p)} remove={() => setItems((l) => l.filter((_, k) => k !== i))} />
            ) : null))}
          </div>
        ))}
        {isAdmin && <Button size="sm" variant="outline" data-testid="cct-add-group" onClick={addGroup} className="gap-1"><FolderPlus className="w-4 h-4" />Add compliance group</Button>}
      </div>
      <FeeOverrides items={items} isAdmin={isAdmin} labels={Object.fromEntries(groups.map((g) => [g.key, g.label]))} />
    </div>
  );
}
